import React from "react";
import { useLocalization } from "utilities/hooks/use-localization";
import DataTable, { DataTableStyle } from "organisms/data-tables/data-table";
import { PageAccessEntryRecord } from "models/view-models/page-access-entry-record";
import Button from "atoms/buttons/button";
import { ButtonStyles } from "atoms/constants/button-styles";
import { Icons } from "atoms/constants/icons";
import Icon from "atoms/icons/icon";
import { IconSizes } from "atoms/constants/icon-sizes";

interface PageAccessRecipientsTableProps {
    loading: boolean;
    recipients: PageAccessEntryRecord[];
    removeRecipient: (recipient: PageAccessEntryRecord) => void;
}

const CSS_CLASS_NAME: string = "c-page-access-recipients-table";

export const PageAccessRecipientsTable: React.FC<
    PageAccessRecipientsTableProps
> = ({
    loading,
    recipients,
    removeRecipient,
}: PageAccessRecipientsTableProps): JSX.Element => {
    const { t } = useLocalization();
    const accessText = (recipient: PageAccessEntryRecord) => {
        if (!recipient.expiresOn) {
            return (
                <span className={`${CSS_CLASS_NAME}__doesNotExpire`}>
                    {t("managePageAccess-no-expiration")}
                </span>
            );
        }
        if (recipient.accessExpired()) {
            return (
                <span className={`${CSS_CLASS_NAME}__expired`}>
                    {t("managePageAccess-accessExpired")}
                </span>
            );
        }
        return (
            <span className={`${CSS_CLASS_NAME}__expirationDate`}>
                {recipient.getExpirationDay()}
            </span>
        );
    };
    return (
        <>
            <div className={`${CSS_CLASS_NAME}__recipients-list`}>
                <DataTable
                    style={DataTableStyle.Compact}
                    cssClassName={`${CSS_CLASS_NAME}__share-recipient-table`}>
                    <thead className="sr-only">
                        <tr>
                            <th>{t("email")}</th>
                            <th>
                                {t("managePageAccess-public-access-expires")}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {!loading &&
                            recipients.map((recipient) => {
                                const rowKey =
                                    recipient.getSourceId() != null
                                        ? `${recipient.source}-${recipient.getSourceId()}`
                                        : `${recipient.source}-${recipient.email}`;

                                return (
                                    <tr
                                        className={`${CSS_CLASS_NAME}__row`}
                                        key={rowKey}>
                                        <td
                                            className={`${CSS_CLASS_NAME}__email`}>
                                            {recipient.email}
                                        </td>
                                        <td
                                            className={`${CSS_CLASS_NAME}__management`}>
                                            {accessText(recipient)}
                                            <Button
                                                accessibleText={t(
                                                    "removeAccessFor",
                                                    {
                                                        user: recipient.email,
                                                    }
                                                )}
                                                cssClassName={`${CSS_CLASS_NAME}__delete-button`}
                                                onClick={() =>
                                                    removeRecipient(recipient)
                                                }
                                                style={
                                                    ButtonStyles.Destructive
                                                }>
                                                <Icon
                                                    type={Icons.Trashcan}
                                                    size={IconSizes.Small}
                                                />
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                    </tbody>
                </DataTable>
            </div>
        </>
    );
};
